/**
 * @flow
 */

import React, { useState } from 'react';
import { Modal, Header, Button, Icon, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { get } from 'lodash';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { format, addMonths, startOfMonth } from 'date-fns';

import { GET_SALON_QUERY } from '../../containers/timetable/MasterTable';
import { GET_MASTER_APPOINTMENTS_QUERY } from '../../../master-appointments/containers/queries';
import { GET_WORKING_DAYS } from '../../../master-appointments/containers/DateRange';

import type { Appointment } from '../../../../type';

const DELETE_APPOINTMENT_QUERY = gql`
    mutation deleteAppointment($input: DeleteAppointmentInput!) {
        deleteAppointment(input: $input) {
            appointmentId
        }
    }
`;

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.schedule.form.AppointmentForm.existingsError',
        defaultMessage: 'This appointment does not exist',
    },
    permissionsError: {
        id: 'pages.appointments.details.DeleteAppointmentDialog.permissionsError',
        defaultMessage:
            'Current user does not have permission to remove paid invoices',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const updateAppointments = (
    store,
    salon,
    appointment,
    showArchive,
    currentLanguage,
) => {
    try {
        const data = store.readQuery({
            query: GET_SALON_QUERY,
            variables: {
                salon,
                showArchive,
                date: format(new Date(appointment.startAt), 'yyyy-MM-dd'),
                lang: currentLanguage,
            },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.masters &&
            data.viewer.salon.masters.nodes
        ) {
            const updatedMastersNodes = data.viewer.salon.masters.nodes.map(
                master => {
                    if (master.id !== appointment.master.id) {
                        return master;
                    }

                    const deletedAppointment = master.appointments.find(
                        item => item.id === appointment.id,
                    );
                    const group = deletedAppointment ? deletedAppointment.group : null;
                    let remainingAppointments = master.appointments.filter(
                        item => item.id !== appointment.id,
                    );

                    if (group) {
                        const groupedAppointments = remainingAppointments.filter(
                            item => item.group === group,
                        );
                        if (groupedAppointments.length === 1) {
                            remainingAppointments = remainingAppointments.map(item => {
                                if (item.group === group) {
                                    return { ...item, group: null };
                                }
                                return item;
                            });
                        }
                    }

                    return {
                        ...master,
                        appointments: remainingAppointments,
                    };
                },
            );

            store.writeQuery({
                query: GET_SALON_QUERY,
                variables: {
                    salon,
                    showArchive,
                    date: format(new Date(appointment.startAt), 'yyyy-MM-dd'),
                    lang: currentLanguage,
                },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            masters: {
                                ...data.viewer.salon.masters,
                                nodes: updatedMastersNodes,
                            },
                        },
                    },
                },
            });
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const updateMasterAppointments = (
    store,
    master,
    salon,
    appointment,
    fromDate,
    toDate,
    showArchive,
    currentLanguage,
) => {
    try {
        const data = store.readQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables: {
                master,
                salon,
                fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                withMaster: true,
                showArchive,
                lang: currentLanguage,
            },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.master &&
            data.viewer.salon.master.appointments
        ) {
            const deletedAppointment = data.viewer.salon.master.appointments.find(
                item => item.id === appointment.id,
            );
            const group = deletedAppointment ? deletedAppointment.group : null;
            let remainingAppointments = data.viewer.salon.master.appointments.filter(
                item => item.id !== appointment.id,
            );

            if (group) {
                const groupedAppointments = remainingAppointments.filter(
                    item => item.group === group,
                );
                if (groupedAppointments.length === 1) {
                    remainingAppointments = remainingAppointments.map(item => {
                        if (item.group === group) {
                            return { ...item, group: null };
                        }
                        return item;
                    });
                }
            }

            store.writeQuery({
                query: GET_MASTER_APPOINTMENTS_QUERY,
                variables: {
                    master,
                    salon,
                    fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
                    toDate: format(new Date(toDate), 'yyyy-MM-dd'),
                    withMaster: true,
                    showArchive,
                    lang: currentLanguage,
                },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            master: {
                                ...data.viewer.salon.master,
                                appointments: remainingAppointments,
                            },
                        },
                    },
                },
            });
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const updateWorkingDays = (store, salon, master, appointment) => {
    const fromDate = format(new Date(), 'yyyy-MM-dd');
    const toDate = format(addMonths(startOfMonth(new Date()), 6), 'yyyy-MM-dd');
    try {
        const data = store.readQuery({
            query: GET_WORKING_DAYS,
            variables: { salon, master, fromDate, toDate },
        });

        if (
            data &&
            data.viewer &&
            data.viewer.salon &&
            data.viewer.salon.workingDays
        ) {
            const dateStr = format(new Date(appointment.startAt), 'yyyy-MM-dd');
            const workingDaysIndex = data.viewer.salon.workingDays.findIndex(
                item => item.date === dateStr,
            );
            if (workingDaysIndex !== -1) {
                let workingDays = [...data.viewer.salon.workingDays];
                const wDate = workingDays[workingDaysIndex];
                if (wDate.appointmentCount > 1) {
                    workingDays[workingDaysIndex] = {
                        ...wDate,
                        appointmentCount: wDate.appointmentCount - 1,
                    };
                } else {
                    workingDays = workingDays.filter(item => item.date !== dateStr);
                }
                store.writeQuery({
                    query: GET_WORKING_DAYS,
                    variables: { salon, master, fromDate, toDate },
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            salon: {
                                ...data.viewer.salon,
                                workingDays,
                            },
                        },
                    },
                });
            }
        }
    } catch (e) {
        // cache not populated yet — safe to ignore
    }
};

const MessageBlock = styled(Message)`
    background-color: transparent !important;
    border: 2px solid #ff4545;
    color: #ff4545 !important;
    font-weight: bold;
    box-shadow: none !important;
`;

type DeleteAppointmentDialogProps = {
    appointment: Appointment,
    trigger: any,
    open: boolean,
    onClose: Function,
    masterAppointments: any,
    fromDate: ?string,
    toDate: ?string,
    intl: Object,
};

function DeleteAppointmentDialog({
    appointment,
    trigger,
    open,
    onClose,
    masterAppointments,
    fromDate,
    toDate,
    intl,
}: DeleteAppointmentDialogProps) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const userId = useSelector(({ user }) => user.get('id'));
    const salonId = useSelector(({ user }) => user.get('salon'));
    const showArchive = useSelector(({ salon: s }) => s.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl: i }) => i.get('locale') || i.get('defaultLanguage'),
    );

    const [deleteAppointmentMutation] = useMutation(DELETE_APPOINTMENT_QUERY);

    const handleDelete = async () => {
        setSubmitting(true);
        setError(null);
        try {
            await deleteAppointmentMutation({
                variables: {
                    input: {
                        userId,
                        appointmentId: appointment.id,
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    deleteAppointment: {
                        __typename: 'DeleteAppointmentPayload',
                        appointmentId: appointment.id,
                    },
                },
                update: store => {
                    if (masterAppointments) {
                        updateMasterAppointments(
                            store,
                            appointment.master.id,
                            salonId,
                            appointment,
                            fromDate,
                            toDate,
                            showArchive,
                            currentLanguage,
                        );
                        updateWorkingDays(
                            store,
                            salonId,
                            appointment.master.id,
                            appointment,
                        );
                    } else {
                        updateAppointments(
                            store,
                            salonId,
                            appointment,
                            showArchive,
                            currentLanguage,
                        );
                    }
                },
            });
            onClose();
        } catch (err) {
            const graphQLError = err.graphQLErrors && err.graphQLErrors[0];
            if (graphQLError) {
                if (
                    graphQLError.name === 'NotExists' &&
                    get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS'
                ) {
                    setError(intl.formatMessage(intlMessages.existingsError));
                } else if (
                    graphQLError.name === 'Forbidden' &&
                    get(graphQLError, 'data.error') ===
                        'USER_DOES_NOT_HAVE_PERMISSION_TO_REMOVE'
                ) {
                    setError(intl.formatMessage(intlMessages.permissionsError));
                } else {
                    setError(intl.formatMessage(intlMessages.defaultErrorMessage));
                }
            } else {
                setError(intl.formatMessage(intlMessages.defaultErrorMessage));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.appointments.details.DeleteAppointmentDialog.title"
                    defaultMessage="Delete Appointment"
                />
            </Header>
            <Modal.Content>
                <FormattedMessage
                    id="pages.appointments.AppointmentDetails.deleteMessage"
                    defaultMessage="Are you sure you want to delete appointment for {clientFirstName} {clientLastName} with {masterFirstName} {masterLastName}?"
                    values={{
                        clientFirstName: get(appointment, 'client.firstName'),
                        clientLastName: get(appointment, 'client.lastName'),
                        masterFirstName: get(appointment, 'master.firstName'),
                        masterLastName:
                            get(appointment, 'master.lastName') ||
                            `${get(appointment, 'cabinet.name') || ''}`,
                    }}
                    tagName="p"
                />
                {error && (
                    <MessageBlock error>
                        <Icon name="warning sign" />
                        {error}
                    </MessageBlock>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.appointments.details.DeleteAppointmentDialog.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button
                    onClick={handleDelete}
                    disabled={submitting}
                    color="green"
                    inverted
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.appointments.details.DeleteAppointmentDialog.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default injectIntl(DeleteAppointmentDialog);
